/**
 * Feature flags. To add one, define it below with an owner and an expiry date
 * (`permanent` only for operational switches like `ciRunner`), then read it.
 * Remove it once the feature has fully shipped; `pnpm check:feature-flags`
 * fails after it expires. Admins change overrides through the
 * `adminSetFeatureFlag` callable, which also maintains the admin-only
 * `featureFlagTargets` index and `featureFlagAuditLog`.
 *
 * The definitions below are the source of truth for which flags exist and
 * what they default to. Firestore only stores overrides:
 *
 * - `featureFlagOverrides` on an app document (`users/{uid}/applications/{id}`)
 * - `featureFlagOverrides` on a user document (`users/{uid}`)
 * - `globals/featureFlags`, which holds a value per flag and an optional
 *   percentage of apps (or users) that get it
 *
 * An override wins in that order, then the definition's default. Overrides
 * that are not valid for a flag are ignored, so bad data falls back to the
 * next level instead of breaking callers.
 *
 * This is unrelated to `IUser.featureFlags`, which ToDesktop Builder owns and
 * users can write themselves.
 */
export const featureFlagDefinitions = {
    accountTeams: {
        default: false,
        description: 'Teams on existing accounts: the team setup page, All apps grouped by team, Team settings and Your profile, Admins, shared teams and app transfer. Invitation review and accept-before-access apply to everyone.',
        expires: '2027-04-30',
        owner: 'dave@todesktop.com',
    },
    blockRebuildingReleasedVersions: {
        default: false,
        description: 'Fail CLI builds of a version that has already been released, before they replace the files its update feed points to. When off, those builds only log a warning.',
        expires: '2027-04-14',
        owner: 'dave@todesktop.com',
    },
    buildPageRedesign: {
        default: false,
        description: 'Show the redesigned build page: what needs attention first, then the build by stage (source, build, verify, release), with releasing from a dialog.',
        expires: '2027-01-31',
        owner: 'dave@todesktop.com',
    },
    buildsListRedesign: {
        default: false,
        description: 'Show the redesigned Builds page: release summary cards and builds grouped by app version.',
        expires: '2027-01-31',
        owner: 'dave@todesktop.com',
    },
    ciRunner: {
        default: 'circle',
        description: 'CI provider that runs builds. Also reads the legacy app.meta.ciRunner, user.runner and globals/switches.runner fields.',
        expires: 'permanent',
        owner: 'dave@todesktop.com',
        variants: ['circle', 'azure'],
    },
    emailProvider: {
        default: 'postmark',
        description: 'Service that sends invite, security, build and release emails. Invites follow the inviting account, build and release emails the app, and security notifications only the global setting.',
        expires: 'permanent',
        owner: 'dave@todesktop.com',
        variants: ['postmark', 'resend'],
    },
    gettingStartedChecklist: {
        default: false,
        description: 'Show the live getting started checklist on the app overview until the app is first released, instead of the static onboarding tabs.',
        expires: '2027-04-07',
        owner: 'dave@todesktop.com',
    },
    sso: {
        default: false,
        description: 'Single sign-on with Okta (OIDC or SAML) or Google Workspace. Turn it on per team account (its ID): it shows the team Single sign-on settings and the Manage SSO permission, and only teams with it can connect an identity provider or claim a domain.',
        expires: '2027-06-30',
        owner: 'dave@todesktop.com',
    },
    systemTheme: {
        default: true,
        description: 'Let users pick system, light or dark from the account menu (system by default) instead of always using light mode. Turn it off to force light mode.',
        expires: '2027-01-31',
        owner: 'dave@todesktop.com',
    },
    v2AppLab: {
        default: false,
        description: "Show the App lab on v2 apps' builds: a build installed fresh on a machine per platform, driven by commands in plain English a computer use model carries out, with the screens live. Needs v2SmokeTests (and v2AppTests to save a lab as an app test). Plan: v2/smoke/APP-LAB.md.",
        expires: '2027-06-30',
        owner: 'dave@todesktop.com',
    },
    v2Apps: {
        default: false,
        description: 'Offer ToDesktop v2 apps (any prebuilt desktop app, with rollouts) when creating an app, and let the account set up v2 signing. Turn it on per account: the stage needs the v2 services deployed first (v2/PROGRESS.md).',
        expires: '2027-06-30',
        owner: 'dave@todesktop.com',
    },
    v2AppTests: {
        default: false,
        description: "Show v2 apps' app tests in the dashboard: steps in plain English a computer use model runs in smoke runs, edited in Settings › Smoke tests. Needs v2SmokeTests. Plan: v2/smoke/APP-TESTS.md.",
        expires: '2027-06-30',
        owner: 'dave@todesktop.com',
    },
    v2SmokeTests: {
        default: false,
        description: "Show v2 apps' smoke tests in the dashboard: results on builds, the release gate and Settings › Smoke tests. Without it they're hidden, and apps that never turned them on don't run them. Plan: v2/smoke/README.md.",
        expires: '2027-06-30',
        owner: 'dave@todesktop.com',
    },
    // WARNING: DO NOT ENABLE THIS FLAG YET.
    //
    // When it is on, sign-up calls createNewUserNewTeamBatch
    // (packages/web-app/src/~reusables/firebase/usersTeamsCollection.ts), which
    // writes to the top-level `teams` and `users_teams` collections from the
    // client. packages/web-app/firestore.rules has no rules for either
    // collection, so Firestore denies those writes. The batch isn't awaited, so
    // sign-up still looks successful but no team is created.
    //
    // Before enabling, move team creation into a server-side callable or add
    // narrowly scoped rules for both collections, with rules tests. Until then
    // this flag only replaces the old hard-coded `enableTeamsFeature = false`.
    teams: {
        default: false,
        description: 'Create a team for new users when they sign up. Do not enable: the teams collections have no Firestore rules yet, so team creation fails.',
        expires: '2027-04-06',
        owner: 'dave@todesktop.com',
    },
};
export const featureFlagPaths = {
    auditLog: 'featureFlagAuditLog',
    global: 'globals/featureFlags',
    targets: (key) => `featureFlagTargets/${key}`,
};
export const featureFlagKeys = Object.keys(featureFlagDefinitions);
export function isFeatureFlagKey(key) {
    return (typeof key === 'string' && featureFlagKeys.includes(key));
}
export function isValidFeatureFlagValue(key, value) {
    const definition = featureFlagDefinitions[key];
    if ('variants' in definition) {
        return typeof value === 'string' && definition.variants.includes(value);
    }
    return typeof value === 'boolean';
}
/** Whole numbers only: subjects fall into 100 integer buckets */
export function isValidFeatureFlagPercentage(value) {
    return Number.isInteger(value) && Number(value) >= 0 && Number(value) <= 100;
}
/**
 * Stable bucket in [0, 100) for a subject. The flag key is part of the hash
 * so each flag rolls out to a different cohort.
 */
export function featureFlagBucket(key, subjectId) {
    // 32-bit FNV-1a; synchronous and identical in the browser and Node
    /* oxlint-disable no-bitwise */
    let hash = 0x811c9dc5;
    const input = `${key}:${subjectId}`;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0) % 100;
    /* oxlint-enable no-bitwise */
}
function getOverride(key, overrides) {
    if (!overrides || typeof overrides !== 'object')
        return undefined;
    if (!Object.keys(overrides).includes(key))
        return undefined;
    const value = overrides[key];
    return isValidFeatureFlagValue(key, value) ? { value } : undefined;
}
function getGlobal(key, global, subjectId) {
    if (!global || typeof global !== 'object')
        return undefined;
    if (!Object.keys(global).includes(key))
        return undefined;
    const setting = global[key];
    if (!setting || typeof setting !== 'object')
        return undefined;
    const { percentage, value } = setting;
    if (!isValidFeatureFlagValue(key, value))
        return undefined;
    if (percentage === undefined || percentage === null)
        return { value };
    if (!isValidFeatureFlagPercentage(percentage))
        return undefined;
    if (percentage >= 100)
        return { value };
    if (!subjectId)
        return undefined;
    return featureFlagBucket(key, subjectId) < percentage ? { value } : undefined;
}
/** Resolves a flag and reports which level its value came from */
export function explainFeatureFlag(key, context = {}) {
    var _a;
    const { app, global, user } = context;
    const appOverride = getOverride(key, app === null || app === void 0 ? void 0 : app.featureFlagOverrides);
    if (appOverride)
        return { source: 'app', value: appOverride.value };
    const userOverride = getOverride(key, user === null || user === void 0 ? void 0 : user.featureFlagOverrides);
    if (userOverride)
        return { source: 'user', value: userOverride.value };
    const globalSetting = getGlobal(key, global, (_a = app === null || app === void 0 ? void 0 : app.id) !== null && _a !== void 0 ? _a : user === null || user === void 0 ? void 0 : user.id);
    if (globalSetting)
        return { source: 'global', value: globalSetting.value };
    return {
        source: 'default',
        value: featureFlagDefinitions[key].default,
    };
}
export function resolveFeatureFlag(key, context = {}) {
    return explainFeatureFlag(key, context).value;
}
